import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Sparkles, Home, Building2, Building, Check, Phone, Mail, Clock, Shield, Calendar } from 'lucide-react';
import CookieConsent from '../components/CookieConsent';

const LandingPage = () => {
  const [activeService, setActiveService] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      icon: Home,
      title: 'Čiščenje stanovanj in hiš',
      description: 'Redno in generalno čiščenje vašega doma z najnovejšimi metodami in proverjenimi čistili.',
    },
    {
      icon: Building2,
      title: 'Čiščenje apartmajev',
      description: 'Hitro in učinkovito čiščenje pri menjavi gostov. Zagotovimo popolno čistočo za vaše goste.',
    },
    {
      icon: Building2,
      title: 'Poslovni prostori',
      description: 'Profesionalno čiščenje pisarn, poslovnih prostorov in komercialnih objektov.',
    },
    {
      icon: Building,
      title: 'Stopnišča in skupni prostori',
      description: 'Redno vzdrževanje stopnišč in skupnih prostorov v stanovanjskih stavbah.',
    },
  ];

  const pricing = [
    {
      title: 'Osnovno čiščenje',
      subtitle: 'Menjava gostov',
      price: 'od 25€ do 45€',
      priceNote: 'odvisno od velikosti',
      features: [
        'Menjava posteljnine in brisač',
        'Praznjenje smeti',
        'Brisanje vseh površin',
        'Temeljito čiščenje kuhinje',
        'Čiščenje kopalnice',
        'Sesanje tal',
        'Mokro čiščenje tal',
        'Zračenje prostora',
      ],
    },
    {
      title: 'Generalno čiščenje',
      subtitle: 'Temeljito čiščenje',
      price: 'od 3,5€ do 5€ /m²',
      priceNote: '',
      features: [
        'Temeljito čiščenje kopalnice',
        'Temeljito čiščenje kuhinje',
        'Pranje oken',
        'Brisanje notranjosti omar',
        'Odstranjevanje vodnega kamna',
        'Brisanje svetil',
        'Brisanje radiatorjev',
        'Vse iz osnovnega čiščenja',
      ],
    },
  ];

  const additionalServices = [
    { name: 'Čiščenje oken', price: '8€/okno' },
    { name: 'Čiščenje hladilnika', price: '12€' },
    { name: 'Čiščenje pečice', price: '15€ - 20€' },
    { name: 'Čiščenje kuhinjske nape', price: '10€ - 15€' },
    { name: 'Čiščenje pomivalnega stroja', price: '10€' },
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Delujemo 7 dni v tednu',
      description: 'Na voljo smo vsak dan, vključno z vikendi in prazniki.',
    },
    {
      icon: Calendar,
      title: 'Fleksibilni termini',
      description: 'Prilagodimo se vašim potrebam in urniku.',
    },
    {
      icon: Shield,
      title: 'Preverjena čistila',
      description: 'Uporabljamo samo kakovostna in učinkovita čistilna sredstva.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_72222507-eaf6-4541-89f2-ccf4a84b610a/artifacts/7dt89fmo_IMG_2271.jpeg" 
                alt="Crystal Logo" 
                className="h-12 w-12 object-cover rounded-full"
              />
              <span className="text-2xl font-bold text-slate-800">Crystal</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('storitve')} className="text-slate-600 hover:text-cyan-600 transition-colors font-medium">Storitve</button>
              <button onClick={() => scrollToSection('cenik')} className="text-slate-600 hover:text-cyan-600 transition-colors font-medium">Cenik</button>
              <button onClick={() => scrollToSection('o-nas')} className="text-slate-600 hover:text-cyan-600 transition-colors font-medium">O nas</button>
              <Button onClick={() => scrollToSection('kontakt')} className="bg-cyan-600 hover:bg-cyan-700 text-white transition-all duration-300 hover:shadow-lg">
                Kontakt
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-cyan-50 px-4 py-2 rounded-full mb-6 border border-cyan-200">
              <Sparkles className="h-4 w-4 text-cyan-600" />
              <span className="text-sm font-medium text-cyan-700">Profesionalno čiščenje v Sloveniji</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              Profesionalno čiščenje,<br />
              <span className="text-cyan-600">ki vaši nepremičnini</span><br />
              <span className="text-cyan-600">povrne sijaj</span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Zaupajte nam čiščenje vaših apartmajev, hiš in poslovnih prostorov. 
              Zagotavljamo vrhunsko kakovost in zanesljivost.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => scrollToSection('kontakt')} 
                size="lg" 
                className="bg-cyan-600 hover:bg-cyan-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                Naročite čiščenje
              </Button>
              <Button 
                onClick={() => scrollToSection('cenik')} 
                size="lg" 
                variant="outline" 
                className="border-2 border-slate-300 text-slate-700 hover:border-cyan-600 hover:text-cyan-600 text-lg px-8 py-6 transition-all duration-300"
              >
                Oglejte si cenik
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="storitve" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Naše storitve</h2>
            <p className="text-lg text-slate-600">Širok spekter čistilnih storitev za vse vaše potrebe</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="border-2 border-slate-200 hover:border-cyan-500 transition-all duration-300 hover:shadow-xl cursor-pointer group"
                  onMouseEnter={() => setActiveService(index)}
                  onMouseLeave={() => setActiveService(null)}
                >
                  <CardContent className="p-6">
                    <div className="mb-4 inline-flex p-3 bg-cyan-50 rounded-lg group-hover:bg-cyan-100 transition-colors duration-300">
                      <Icon className="h-8 w-8 text-cyan-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="cenik" className="py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Cenik storitev</h2>
            <p className="text-lg text-slate-600">Pregledne in konkurenčne cene</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {pricing.map((plan, index) => (
              <Card key={index} className="border-2 border-slate-200 hover:border-cyan-500 transition-all duration-300 hover:shadow-2xl">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">{plan.title}</h3>
                    <p className="text-slate-600 mb-4">{plan.subtitle}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-cyan-600">{plan.price}</span>
                    </div>
                    {plan.priceNote && (
                      <p className="text-sm text-slate-500 mt-1">{plan.priceNote}</p>
                    )}
                  </div>
                  <div className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Dodatne storitve</h3>
            <Card className="border-2 border-slate-200">
              <CardContent className="p-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  {additionalServices.map((service, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg hover:bg-cyan-50 transition-colors duration-300">
                      <span className="font-medium text-slate-700">{service.name}</span>
                      <span className="font-bold text-cyan-600">{service.price}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="o-nas" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Zakaj izbrati Crystal?</h2>
            <p className="text-lg text-slate-600">Vaše zadovoljstvo je naša prioriteta</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex p-4 bg-cyan-50 rounded-2xl mb-4">
                    <Icon className="h-12 w-12 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Pogosta vprašanja</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-cyan-600 transition-colors">
                Kako lahko naročim čiščenje?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Čiščenje lahko naročite preko telefona na +386 70 363 274 ali +386 64 103 449, ali pa nam pišite na e-naslov crystalstarcic@gmail.com. Z veseljem se bomo dogovorili za termin, ki vam najbolj ustreza.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="bg-white border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-cyan-600 transition-colors">
                Ali ste na voljo tudi ob vikendih?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Da! Delamo 7 dni v tednu, vključno z vikendi in prazniki. Razumemo, da ima vsak drugačen urnik, zato smo tu za vas, ko nas potrebujete.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="bg-white border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-cyan-600 transition-colors">
                Kakšna čistila uporabljate?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Uporabljamo samo proverjena in kakovostna čistilna sredstva, ki so učinkovita in varna za uporabo v domačem okolju ter poslovnih prostorih.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="bg-white border-2 border-slate-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-cyan-600 transition-colors">
                Koliko časa traja čiščenje?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Čas čiščenja je odvisen od velikosti prostora in obsega storitve. Osnovno čiščenje apartmaja običajno traja 2-3 ure, generalno čiščenje pa lahko traja dlje, odvisno od stanja prostora.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-20 px-4 bg-gradient-to-b from-white to-cyan-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Stopite v stik z nami</h2>
            <p className="text-lg text-slate-600">Pripravljeni smo odgovoriti na vsa vaša vprašanja</p>
          </div>
          <Card className="border-2 border-slate-200 shadow-xl">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-cyan-50 rounded-lg">
                      <Phone className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2">Telefon</h3>
                      <a href="tel:+38670363274" className="text-cyan-600 hover:text-cyan-700 block transition-colors">+386 70 363 274</a>
                      <a href="tel:+38664103449" className="text-cyan-600 hover:text-cyan-700 block transition-colors">+386 64 103 449</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-cyan-50 rounded-lg">
                      <Mail className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2">E-pošta</h3>
                      <a href="mailto:crystalstarcic@gmail.com" className="text-cyan-600 hover:text-cyan-700 transition-colors">crystalstarcic@gmail.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-cyan-50 rounded-lg">
                      <Clock className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-2">Delovni čas</h3>
                      <p className="text-slate-600">7 dni v tednu</p>
                      <p className="text-slate-600">Tudi vikendi in prazniki</p>
                    </div>
                  </div>
                </div>
                <div className="bg-cyan-50 rounded-xl p-6 flex items-center justify-center">
                  <div className="text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_72222507-eaf6-4541-89f2-ccf4a84b610a/artifacts/7dt89fmo_IMG_2271.jpeg" 
                      alt="Crystal Logo" 
                      className="h-32 w-32 mx-auto object-cover mb-4 rounded-full"
                    />
                    <h3 className="text-2xl font-bold text-slate-900">Crystal</h3>
                    <p className="text-slate-600 mt-2">Profesionalno čiščenje</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_72222507-eaf6-4541-89f2-ccf4a84b610a/artifacts/7dt89fmo_IMG_2271.jpeg" 
                  alt="Crystal Logo" 
                  className="h-10 w-10 object-cover rounded-full"
                />
                <span className="text-xl font-bold">Crystal</span>
              </div>
              <p className="text-slate-400 mb-4">Profesionalno čiščenje apartmajev, hiš in poslovnih prostorov v Sloveniji.</p>
              <div className="text-sm text-slate-400 space-y-1">
                <p className="font-semibold text-white">Crystal, Aleks Starčič s.p.</p>
                <p>Vinogradniška pot 29</p>
                <p>6280 Ankaran - Ancarano</p>
                <p>Matična številka: 7353561000</p>
                <p>Davčna številka: 84417617</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Storitve</h4>
              <ul className="space-y-2 text-slate-400">
                <li>Čiščenje apartmajev</li>
                <li>Čiščenje hiš in stanovanj</li>
                <li>Poslovni prostori</li>
                <li>Generalno čiščenje</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Kontakt</h4>
              <ul className="space-y-2 text-slate-400">
                <li>+386 70 363 274</li>
                <li>+386 64 103 449</li>
                <li>crystalstarcic@gmail.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p>&copy; 2025 Crystal, Aleks Starčič s.p. Vse pravice pridržane.</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

export default LandingPage;
